import React, { useCallback, useMemo } from "react";
import { Props, Rnd } from "react-rnd";
import { useWinStore } from "./windowStore";
import { WindowContextProvider } from "./WindowContext";
import "../../pages/shared/components/floatingStyles.css";

export const WindowFrame: React.FC<Props> = ({ win }) => {
  const {
    id,
    title,
    x,
    y,
    width,
    height,
    z,
    minimized,
    maximized,
    content,
    noHeader
  } = win;
  const { focus, setPos, setSize, close, toggleMinimize, toggleMaximize } =
    useWinStore((s) => s);

  const onFocus = useCallback(() => {
    focus(id);
  }, [focus, id]);
  const onDragStop = useCallback(
    (_, d) => {
      setPos(id, d.x, d.y);
    },
    [id, setPos]
  );
  const onResizeStop = useCallback(
    (_, __, ref, ___, position) => {
      setSize(id, ref.offsetWidth, ref.offsetHeight);
      setPos(id, position.x, position.y);
    },
    [id, setSize, setPos]
  );

  const handleHeaderDoubleClick = useCallback(() => {
    toggleMaximize(id);
  }, [id, toggleMaximize]);

  const style = useMemo<React.CSSProperties>(
    () => ({
      zIndex: z,
      boxShadow: "0 10px 30px rgba(0,0,0,.25)",
      borderRadius: 12,
      overflow: "hidden",
      background: "#ffffffff",
      border: "1px solid rgba(255,255,255,.08)",
      display: minimized ? "none" : "block",
      pointerEvents: "auto"
    }),
    [z, minimized]
  );

  return (
    <Rnd
      bounds="parent"
      size={{ width, height }}
      position={{ x, y }}
      onDragStart={onFocus}
      onDragStop={onDragStop}
      onResizeStart={onFocus}
      onResizeStop={onResizeStop}
      style={style}
      enableResizing={!maximized}
      disableDragging={maximized}
      dragHandleClassName={noHeader ? "win-email-header" : "win-header"}
      minWidth={280}
      minHeight={160}
    >
      <WindowContextProvider id={id}>
        <div
          onMouseDown={onFocus}
          className="win-root"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column"
          }}
        >
          {!noHeader && (
            <div
              className="win-header"
              onDoubleClick={handleHeaderDoubleClick}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "5px 13px",
                gap: 8,
                background: "#1f2b45",
                color: "rgba(255, 255, 255, 1)",
                cursor: maximized ? "default" : "move",
                userSelect: "none"
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center"
                }}
              >
                {typeof title === "string" ? (
                  <div
                    style={{
                      fontWeight: 600,
                      opacity: 0.9,
                      letterSpacing: 0.2
                    }}
                  >
                    {title}
                  </div>
                ) : React.isValidElement(title) &&
                  typeof title.type !== "string" ? (
                  React.cloneElement(title, { isMinimized: minimized } as any)
                ) : (
                  title
                )}
              </div>

              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }} />
              <div
                className="buttonContainer"
                style={{
                  display: "flex",
                  gap: 8,
                  marginRight: 8,
                  cursor: "pointer",
                  fontSize: "20px"
                }}
              >
                <span
                  className="windowButton"
                  aria-label="Minimize"
                  onClick={() => toggleMinimize(id)}
                >
                  <i className="fas fa-window-minimize" />
                </span>
                <span
                  className="windowButton"
                  aria-label="Close"
                  onClick={() => close(id)}
                >
                  <i className="fas fa-window-close" />
                </span>
              </div>
            </div>
          )}

          <div className="win-content">
            {content ?? <div style={{ padding: 16 }}>Sin contenido</div>}
          </div>
        </div>
      </WindowContextProvider>
    </Rnd>
  );
};
